import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, RefreshCw, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const empty = { email: "", name: "", role: "user" };

export default function Users() {
    const { user: me } = useAuth();
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [linkDialog, setLinkDialog] = useState(null);

    const load = async () => {
        try { setList((await api.get("/users")).data); }
        catch (err) { toast.error(err.response?.data?.detail || "Failed to load users"); }
    };
    useEffect(() => { load(); }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const startEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name || "", role: u.role || "user" }); setOpen(true); };

    const save = async () => {
        const payload = { email: (form.email || "").toLowerCase().trim(), name: form.name.trim(), role: form.role };
        if (!editing && !payload.email) { toast.error("Email required"); return; }
        try {
            if (editing) {
                await api.put(`/users/${editing.user_id}`, payload);
                toast.success("User updated");
            } else {
                const { data } = await api.post("/users", payload);
                setLinkDialog({ email: payload.email, link: data.activation_link });
                toast.success("User created — share the activation link");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const regen = async (u) => {
        try {
            const { data } = await api.post(`/users/${u.user_id}/regenerate-activation`);
            setLinkDialog({ email: u.email, link: data.activation_link });
            toast.success("New activation link generated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete user ${u.email}?`)) return;
        try {
            await api.delete(`/users/${u.user_id}`);
            toast.success("User deleted");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const copyLink = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Copy failed — select and copy manually");
        }
    };

    return (
        <div>
            <PageHeader
                eyebrow="Admin"
                title="Users & roles"
                subtitle="Add team members and decide who can manage masters and users."
                actions={<Button onClick={startCreate} className="rounded-none bg-[#E63946] hover:bg-[#C92836]" data-testid="add-user-button"><Plus size={16} className="mr-2" /> Add user</Button>}
            />
            <div className="px-8 py-6">
                <table className="w-full sheet-table" data-testid="users-table">
                    <thead>
                        <tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Created</th><th className="text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {list.map((u) => (
                            <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                                <td className="font-mono">{u.email}{u.user_id === me?.user_id && <span className="ml-2 text-[10px] uppercase text-[#2A9D8F]">(you)</span>}</td>
                                <td>{u.name || "—"}</td>
                                <td><span className={`tag-pill ${u.role === "admin" ? "bg-[#E63946] text-white border-[#E63946]" : ""}`}>{u.role}</span></td>
                                <td className="font-mono text-xs">
                                    {u.is_active
                                        ? <span className="text-[#2A9D8F]">Active</span>
                                        : <span className="text-amber-600">Pending activation</span>}
                                </td>
                                <td className="font-mono text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
                                <td className="text-right whitespace-nowrap">
                                    {!u.is_active && (
                                        <button onClick={() => regen(u)} className="text-[#002FA7] hover:underline text-xs uppercase tracking-wider mr-3" data-testid={`regen-activation-${u.user_id}`}>
                                            <RefreshCw size={12} className="inline mr-1" /> Activation
                                        </button>
                                    )}
                                    <button onClick={() => startEdit(u)} className="text-zinc-500 hover:text-[#002FA7] mr-3" data-testid={`edit-user-${u.user_id}`}><Pencil size={14} /></button>
                                    {u.user_id !== me?.user_id && (
                                        <button onClick={() => remove(u)} className="text-zinc-400 hover:text-[#E63946]" data-testid={`delete-user-${u.user_id}`}><Trash2 size={14} /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {list.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-400 italic py-8">No users yet — invite your first teammate.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Add / Edit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2 space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Email *</Label>
                            <Input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} className="rounded-none" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Name</Label>
                            <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger className="rounded-none" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">User (limited)</SelectItem>
                                    <SelectItem value="project_manager">Project Manager (Tasks module only)</SelectItem>
                                    <SelectItem value="task_executor">Task Executor (view + own tasks only)</SelectItem>
                                    <SelectItem value="admin">Admin (full access)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#E63946] hover:bg-[#C92836]" onClick={save} data-testid="user-save-button">{editing ? "Save" : "Create + Generate link"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Activation link dialog */}
            <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
                <DialogContent className="rounded-none max-w-xl">
                    <DialogHeader><DialogTitle>Activation link for {linkDialog?.email}</DialogTitle></DialogHeader>
                    <p className="text-sm text-zinc-500">Share this link with the user — they can set their password and sign in.</p>
                    <div className="border-2 border-[#09090b] p-4 text-xs font-mono break-all bg-zinc-50" data-testid="activation-link-text">
                        {linkDialog?.link}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => copyLink(linkDialog?.link || "")} data-testid="copy-activation-link"><Copy size={14} className="mr-2" /> Copy link</Button>
                        <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={() => setLinkDialog(null)}>Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
